import React from 'react'
import { Helmet } from 'react-helmet'

/**
 * <SEO />
 *
 * Single source of truth for per-page SEO. Emits the full set of tags every
 * page should have:
 *
 *  - <title>
 *  - <meta name="description">
 *  - <meta name="keywords">          (kept for completeness — Google ignores)
 *  - <link rel="canonical">
 *  - Open Graph tags                  (Facebook, LinkedIn, Slack, etc.)
 *  - Twitter card                     (summary_large_image)
 *  - <meta name="robots">             (index/noindex control)
 *  - JSON-LD structured data          (any object passed in via `jsonLd`)
 *
 * Defaults handle the common case so every page just supplies path + title +
 * description. Optional fields override when needed.
 */

const SITE = {
  name: 'QA Tech',
  origin: 'https://qatechltd.com',
  twitter: '@qatechltd',
}

const SEO = ({
  path = '/',
  title,
  description,
  keywords,
  noindex = false,
  type = 'website',
  image, // optional absolute URL of an OG image
  jsonLd,
}) => {
  const url = `${SITE.origin}${path === '/' ? '/' : path.replace(/\/$/, '')}`
  // If the page-supplied title already contains the brand name, keep it as-is
  // so we don't end up with awkward "QA Tech … | QA Tech" duplication. Otherwise
  // append the brand for consistency and recognisability in search results.
  const titleAlreadyHasBrand = title && /qa\s*tech/i.test(title)
  const fullTitle = title
    ? titleAlreadyHasBrand
      ? title
      : `${title} | ${SITE.name}`
    : `${SITE.name} — London Marketing & Software Engineering`

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={url} />
      {image && <meta property="og:image" content={image} />}

      {/* Twitter */}
      <meta name="twitter:card" content={image ? 'summary_large_image' : 'summary'} />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      {image && <meta name="twitter:image" content={image} />}

      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  )
}

export default SEO
